/**
 * A discipline's projects grouped into folders: the built-in Samples folder (where the
 * discipline has samples), the user's own folders, and the projects in none. Every discipline's
 * list uses it, so folders are learned once.
 *
 * A folder is a way of looking at projects (store/project-folders.ts): moving one changes nothing
 * about it, and deleting a folder unfiles what was in it.
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  copyProject,
  copySample,
  resetSample,
  useFolderStore,
  useFolderView,
  type FolderView,
  type ProjectFolder,
} from '../../../store/project-folders';
import { Button } from './Button';
import { TypedConfirm } from './TypedConfirm';

export interface FolderedProject {
  id: string;
  /** The row, given the control that moves it between folders - or, for a sample, the one that
   *  resets and copies it. A sample's row offers no delete: `sample` says which this is. */
  render: (folderControl: ReactNode, options?: { sample?: boolean }) => ReactNode;
}

export interface Sample {
  key: string;
  name: string;
  summary: string;
  teaches?: string[];
  /** Opens the user's own copy - made the first time, reopened after. */
  onOpen: () => void;
}

/** Move a project to another folder, or copy it into one. Always visible - it was a dropdown that
 *  appeared only on hover, and only once a folder existed, so nobody found it. A copy is a new
 *  project with the same content; the original stays where it is. */
function MoveControl({
  discipline,
  projectId,
  current,
  folders,
  onCopied,
}: {
  discipline: string;
  projectId: string;
  current: string | null;
  folders: ProjectFolder[];
  onCopied?: () => void;
}) {
  const move = useFolderStore((s) => s.move);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const targets = [{ id: '', name: 'No folder' }, ...folders];

  async function act(choice: string) {
    const [action, folderId] = [choice.slice(0, 4), choice.slice(5)];
    setError(null);
    if (action === 'move') {
      await move(discipline, projectId, folderId || null);
      return;
    }
    setBusy(true);
    try {
      await copyProject(discipline, projectId, folderId || null);
      onCopied?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <select
        aria-label="Move or copy"
        title="Move this project to a folder, or copy it into one"
        value=""
        disabled={busy}
        onChange={(e) => void act(e.target.value)}
        className="rounded-md border border-gray-200 bg-white px-1.5 py-1 text-xs text-gray-600 hover:border-gray-300"
      >
        <option value="" disabled>
          {busy ? 'Copying…' : 'Move or copy…'}
        </option>
        {folders.length > 0 && (
          <optgroup label="Move to">
            {targets.map((f) => (
              <option key={`m${f.id}`} value={`move:${f.id}`} disabled={(current ?? '') === f.id}>
                {f.name}
                {(current ?? '') === f.id ? ' (here)' : ''}
              </option>
            ))}
          </optgroup>
        )}
        <optgroup label="Copy to">
          {targets.map((f) => (
            <option key={`c${f.id}`} value={`copy:${f.id}`}>
              {f.name}
            </option>
          ))}
        </optgroup>
      </select>
      {error && (
        <span role="alert" className="text-xs text-red-700">
          {error}
        </span>
      )}
    </span>
  );
}

/** A sample's control, in place of moving and deleting: samples cannot be deleted or moved out of
 *  Samples. Changed around, one is RESET to how it shipped; to keep a version, it is COPIED - fresh
 *  from the catalogue - into a folder of the user's own, existing or new, as an ordinary project. */
function SampleControl({
  discipline,
  sampleKey,
  opened,
  folders,
  onChanged,
}: {
  discipline: string;
  sampleKey: string;
  /** Whether the user has their own copy of it yet: there is nothing to reset before. */
  opened: boolean;
  folders: ProjectFolder[];
  onChanged?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [naming, setNaming] = useState(false);
  const [folderName, setFolderName] = useState('');

  async function run(label: string, action: () => Promise<void>) {
    setBusy(label);
    setError(null);
    try {
      await action();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  function copyTo(choice: string) {
    if (choice === 'new') {
      setNaming(true);
      return;
    }
    void run('Copying…', () => copySample(discipline, sampleKey, choice ? { folderId: choice } : {}));
  }

  return (
    <span className="flex shrink-0 flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {naming ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            const name = folderName.trim();
            if (!name) return;
            setNaming(false);
            setFolderName('');
            void run('Copying…', () => copySample(discipline, sampleKey, { folderName: name }));
          }}
        >
          <input
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="New folder name"
            aria-label="New folder for the copy"
            autoFocus
            className="w-36 rounded-md border border-gray-300 px-2 py-1 text-xs"
          />
          <Button size="sm" type="submit" disabled={!folderName.trim()}>
            Copy
          </Button>
          <Button size="sm" type="button" onClick={() => setNaming(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        <select
          aria-label="Copy the sample to a folder"
          title="Copy this sample into a folder of your own - a fresh copy you can change and delete freely"
          value=""
          disabled={busy !== null}
          onChange={(e) => copyTo(e.target.value)}
          className="rounded-md border border-gray-200 bg-white px-1.5 py-1 text-xs text-gray-600 hover:border-gray-300"
        >
          <option value="" disabled>
            {busy ?? 'Copy to…'}
          </option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
          <option value="new">A new folder…</option>
        </select>
      )}
      {opened &&
        (confirmingReset ? (
          <span className="flex items-center gap-1 text-xs text-gray-600">
            Put it back as it was?
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setConfirmingReset(false);
                void run('Resetting…', () => resetSample(discipline, sampleKey));
              }}
            >
              Reset
            </Button>
            <Button size="sm" onClick={() => setConfirmingReset(false)}>
              Cancel
            </Button>
          </span>
        ) : (
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => setConfirmingReset(true)}
            title="Put this sample back as it was - your changes to it are lost"
            className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
          >
            Reset sample
          </button>
        ))}
      {error && (
        <span role="alert" className="text-xs text-red-700">
          {error}
        </span>
      )}
    </span>
  );
}

function FolderHeader({
  title,
  count,
  open,
  onToggle,
  actions,
}: {
  title: ReactNode;
  count: number;
  open: boolean;
  onToggle: () => void;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-w-0 items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-800"
      >
        <span aria-hidden="true" className={clsx('inline-block transition-transform', open && 'rotate-90')}>
          ▸
        </span>
        <span className="truncate">{title}</span>
        <span className="font-normal normal-case tracking-normal text-gray-400">{count}</span>
      </button>
      {actions}
    </div>
  );
}

function UserFolder({
  discipline,
  folder,
  rows,
}: {
  discipline: string;
  folder: ProjectFolder;
  rows: ReactNode[];
}) {
  const renameFolder = useFolderStore((s) => s.renameFolder);
  const deleteFolder = useFolderStore((s) => s.deleteFolder);
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [confirming, setConfirming] = useState(false);
  return (
    <section className="mt-6" aria-label={`Folder ${folder.name}`}>
      {editing ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void renameFolder(discipline, folder.id, name.trim());
            setEditing(false);
          }}
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Folder name"
            autoFocus
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <Button size="sm" type="submit">
            Save
          </Button>
          <Button size="sm" type="button" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        <FolderHeader
          title={folder.name}
          count={rows.length}
          open={open}
          onToggle={() => setOpen(!open)}
          actions={
            confirming ? (
              <TypedConfirm
                phrase="Delete Folder"
                message={rows.length === 0 ? 'Deletes the folder, which is empty.' : `Deletes the folder only. Its ${rows.length === 1 ? 'project stays' : `${rows.length} projects stay`}, under No folder.`}
                action="Delete folder"
                onConfirm={() => void deleteFolder(discipline, folder.id)}
                onCancel={() => setConfirming(false)}
              />
            ) : (
              <span className="flex gap-1">
                <button type="button" onClick={() => setEditing(true)} className="rounded px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-100">
                  Rename
                </button>
                <button type="button" onClick={() => setConfirming(true)} className="rounded px-2 py-0.5 text-xs text-gray-500 hover:bg-red-50 hover:text-red-700">
                  Delete
                </button>
              </span>
            )
          }
        />
      )}
      {open && (
        <ul className="mt-3 space-y-2">
          {rows.length ? rows : <li className="text-xs text-gray-500">Empty - move a project here from its row.</li>}
        </ul>
      )}
    </section>
  );
}

function SampleRow({ sample, control }: { sample: Sample; control: ReactNode }) {
  return (
    <li className="group flex items-start gap-4 rounded-xl border border-dashed border-gray-300 bg-white/70 px-4 py-3">
      <span aria-hidden="true" className="mt-1 h-9 w-1 shrink-0 rounded-full bg-gray-200" />
      <div className="min-w-0 flex-1">
        <span className="block font-display text-[0.95rem] font-semibold text-gray-900">{sample.name}</span>
        <span className="mt-0.5 block text-xs text-gray-600">{sample.summary}</span>
        {sample.teaches && sample.teaches.length > 0 && (
          <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-xs text-gray-500">
            {sample.teaches.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </div>
      {control}
      <Button size="sm" variant="secondary" onClick={sample.onOpen}>
        Open sample
      </Button>
    </li>
  );
}

export function FolderedProjects({
  discipline,
  projects,
  samples,
  isLoading,
  empty,
  view: given,
  onCopied,
}: {
  discipline: string;
  projects: FolderedProject[];
  samples?: Sample[];
  isLoading: boolean;
  empty: ReactNode;
  /** The folders to show, where the caller has them; otherwise the store's. */
  view?: FolderView;
  /** Called after a copy is made, so the page can list it. */
  onCopied?: () => void;
}) {
  const load = useFolderStore((s) => s.load);
  const createFolder = useFolderStore((s) => s.createFolder);
  const error = useFolderStore((s) => s.error);
  const stored = useFolderView(discipline);
  const view = given ?? stored;
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [samplesOpen, setSamplesOpen] = useState(true);

  useEffect(() => {
    void load(discipline);
  }, [load, discipline]);

  const groups = useMemo(() => {
    const known = new Set(view.folders.map((f) => f.id));
    const sampleCopies = new Set(Object.values(view.samples));
    const byFolder: Record<string, FolderedProject[]> = {};
    const unfiled: FolderedProject[] = [];
    for (const project of projects) {
      // A sample is shown in Samples, always - even one moved into a folder before samples stayed put.
      if (samples && sampleCopies.has(project.id)) continue;
      const folder = view.placements[project.id];
      if (folder && known.has(folder)) (byFolder[folder] ??= []).push(project);
      else unfiled.push(project);
    }
    return { byFolder, unfiled };
  }, [projects, view, samples]);

  const row = (project: FolderedProject) => (
    <Fragment key={project.id}>
      {project.render(
        <MoveControl
          discipline={discipline}
          projectId={project.id}
          current={view.placements[project.id] ?? null}
          folders={view.folders}
          onCopied={onCopied}
        />,
      )}
    </Fragment>
  );

  const projectIds = new Set(projects.map((p) => p.id));
  const sampleRows = (samples ?? []).map((sample) => {
    const copy = view.samples[sample.key];
    const project = copy && projectIds.has(copy) ? projects.find((p) => p.id === copy) : undefined;
    const control = (
      <SampleControl
        discipline={discipline}
        sampleKey={sample.key}
        opened={Boolean(project)}
        folders={view.folders}
        onChanged={onCopied}
      />
    );
    // The user's own copy of the sample, opened: its row, with no delete and no move.
    if (project) return <Fragment key={project.id}>{project.render(control, { sample: true })}</Fragment>;
    return <SampleRow key={sample.key} sample={sample} control={control} />;
  });

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Your projects</h2>
        {adding ? (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim()) void createFolder(discipline, newName.trim());
              setNewName('');
              setAdding(false);
            }}
          >
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Folder name"
              aria-label="New folder name"
              autoFocus
              className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
            <Button size="sm" type="submit" disabled={!newName.trim()}>
              Create
            </Button>
            <Button size="sm" type="button" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            New folder
          </Button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}

      {samples && samples.length > 0 && (
        <section className="mt-4" aria-label="Samples">
          <FolderHeader title="Samples" count={samples.length} open={samplesOpen} onToggle={() => setSamplesOpen(!samplesOpen)} />
          {samplesOpen && (
            <>
              <p className="mt-1 text-xs text-gray-500">
                Fictitious companies with invented figures, yours to change. A sample cannot be deleted: reset one to put
                it back as it was, or copy it into a folder of your own to keep a version you can change and delete freely.
              </p>
              <ul className="mt-3 space-y-2">{sampleRows}</ul>
            </>
          )}
        </section>
      )}

      {view.folders.map((folder) => (
        <UserFolder key={folder.id} discipline={discipline} folder={folder} rows={(groups.byFolder[folder.id] ?? []).map(row)} />
      ))}

      <section className="mt-6" aria-label="Unfiled projects">
        {(view.folders.length > 0 || (samples && samples.length > 0)) && (
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Not in a folder</h3>
        )}
        {isLoading && projects.length === 0 && <p className="mt-3 text-sm text-gray-500">Loading…</p>}
        {!isLoading && groups.unfiled.length === 0 && (
          <p className="mt-3 rounded-xl border border-dashed border-gray-300 bg-white/60 px-5 py-6 text-sm text-gray-600">
            {Object.values(groups.byFolder).some((rows) => rows.length > 0) ? 'Every project is in a folder.' : empty}
          </p>
        )}
        <ul className="mt-3 space-y-2">{groups.unfiled.map(row)}</ul>
      </section>
    </div>
  );
}
